// client/src/utils/websocketClient.js

import { io } from 'socket.io-client';
import { WS_CONNECT_TIMEOUT_MS, WS_PING_INTERVAL_MS, WS_RECONNECT_DELAY_MS, WS_MAX_RECONNECT_ATTEMPTS, WS_SYSTEM_SETTLE_MS } from '../constants/time';

class WebSocketClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = WS_MAX_RECONNECT_ATTEMPTS;
    this.eventListeners = new Map();
    this.connectionStatus = {
      isConnected: false,
      connectionCount: 0,
      lastConnected: null,
      lastDisconnected: null
    };
    this.pingInterval = null;
    this.reconnectTimeout = null;
    this.user = null;
  }

  connect(user = null) {
    // Store user for reconnection attempts
    if (user) {
      this.user = user;
    }
    
    // Setup browser event listeners on first connection
    if (!this.eventListenersSetup) {
      this.setupBrowserEventListeners();
      this.eventListenersSetup = true;
    }
    
    // Determine server URL
    const serverUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 
                      (window.location.hostname === 'localhost' ? 'http://localhost:5000' : window.location.origin);
    
    this.socket = io(serverUrl, {
      transports: ['websocket'], // Only use websocket, no polling
      timeout: WS_CONNECT_TIMEOUT_MS,
      forceNew: false, // Don't force new connections
      upgrade: false, // Don't upgrade from polling
      autoConnect: true
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.connectionStatus.isConnected = true;
      this.connectionStatus.lastConnected = new Date().toISOString();
      
      // Clear any pending reconnect
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      
      // Authenticate if user provided
      if (user) {
        this.socket.emit('authenticate', user.id);
      }
      
      // Start health monitoring
      this.startPingInterval();
      
      // Notify app of connection
      this.emit('websocket:connected');
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.connectionStatus.isConnected = false;
      this.connectionStatus.lastDisconnected = new Date().toISOString();
      
      // Stop health monitoring
      this.stopPingInterval();
      
      this.emit('websocket:disconnected', reason);
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.emit('websocket:max_reconnect_failed');
        // Auto-retry after reconnect delay
        this.scheduleReconnect(WS_RECONNECT_DELAY_MS);
      }
    });

    // Listen for real-time updates
    this.socket.on('realTimeUpdate', (update) => {
      this.emit('realtime:update', update);
    });

    // Connection count updates
    this.socket.on('connectionCount', (count) => {
      this.connectionStatus.connectionCount = count;
      this.emit('websocket:connection_count', count);
    });

    // Handle pong responses
    this.socket.on('pong', () => {
      // Pong received - connection is healthy
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.connectionStatus.isConnected = false;
    }
    
    // Clean up intervals and timeouts
    this.stopPingInterval();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  // Send ping to test connection
  ping() {
    if (this.socket && this.isConnected) {
      this.socket.emit('ping');
    }
  }

  // Event emitter functionality
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket event callback:', error);
        }
      });
    }
  }

  // Send data to server
  send(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    }
  }

  // ============= PM (PREVENTIVE MAINTENANCE) METHODS =============
  
  // Join PM area room for real-time updates during task execution
  joinPMArea(areaId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinPMArea', areaId);
    }
  }

  // Leave PM area room
  leavePMArea(areaId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leavePMArea', areaId);
    }
  }

  // Join PM dashboard room for global PM updates
  joinPMDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinPMDashboard');
    }
  }

  // Leave PM dashboard room
  leavePMDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('leavePMDashboard');
    }
  }

  // Emit PM area change (create, update, delete, clone)
  emitPMAreaChange(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('pmAreaChanged', data);
    }
  }

  // Emit PM task completion
  emitPMTaskCompleted(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('pmTaskCompleted', data);
    }
  }

  // Emit PM schedule update
  emitPMScheduleUpdate(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('pmScheduleUpdated', data);
    }
  }

  // ============= QUALITY MANAGEMENT METHODS =============
  
  // Join Quality dashboard room for real-time updates
  joinQualityDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinQualityDashboard');
    }
  }

  // Leave Quality dashboard room
  leaveQualityDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('leaveQualityDashboard');
    }
  }

  // Emit quality hold info completed
  emitQualityHoldCompleted(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('qualityHoldCompleted', data);
    }
  }

  // Emit quality disposition completed
  emitQualityDispositioned(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('qualityDispositioned', data);
    }
  }

  // Emit quality issue status change
  emitQualityIssueChanged(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('qualityIssueChanged', data);
    }
  }

  // ============= SMED CHANGEOVER DASHBOARD METHODS =============
  
  // Join SMED Dashboard room for real-time updates
  joinSMEDDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinSMEDDashboard');
    }
  }

  // Leave SMED Dashboard room
  leaveSMEDDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('leaveSMEDDashboard');
    }
  }

  // Emit changeover change (scheduled, started, completed, cancelled)
  emitChangeoverChanged(data) {
    if (this.socket && this.isConnected) {
      this.socket.emit('changeoverChanged', data);
    }
  }

  // ============= PARAMETER MANAGEMENT METHODS =============

  // Join machine-specific parameter room for real-time updates
  joinParameterView(machineId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinParameterView', machineId);
    }
  }

  // Leave machine-specific parameter room
  leaveParameterView(machineId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leaveParameterView', machineId);
    }
  }

  // Join global parameter management room (catalog/definitions page)
  joinParameterManagement() {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinParameterManagement');
    }
  }

  // Leave global parameter management room
  leaveParameterManagement() {
    if (this.socket && this.isConnected) {
      this.socket.emit('leaveParameterManagement');
    }
  }

  // ============= USER ACTIVITY TRACKING METHODS =============
  
  // Join Activity dashboard room for admin real-time updates
  joinActivityDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('joinActivityDashboard');
    }
  }

  // Leave Activity dashboard room
  leaveActivityDashboard() {
    if (this.socket && this.isConnected) {
      this.socket.emit('leaveActivityDashboard');
    }
  }

  getConnectionStatus() {
    return {
      ...this.connectionStatus,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Request browser notification permission
  requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  // Health monitoring with ping/pong
  startPingInterval() {
    // Ping periodically to detect connection health
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.ping();
      }
    }, WS_PING_INTERVAL_MS);
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Schedule reconnection attempt
  scheduleReconnect(delay = WS_RECONNECT_DELAY_MS) {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts = 0; // Reset counter for new attempt cycle
      if (this.user) {
        this.connect(this.user);
      }
    }, delay);
  }

  // Setup browser event listeners for automatic reconnection
  setupBrowserEventListeners() {
    // Handle tab visibility changes (sleep/wake, tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.isConnected) {
        setTimeout(() => {
          if (!this.isConnected && this.user) {
            this.connect(this.user);
          }
        }, WS_SYSTEM_SETTLE_MS); // Small delay to let system settle
      }
    });

    // Handle online/offline events
    window.addEventListener('online', () => {
      if (!this.isConnected && this.user) {
        this.connect(this.user);
      }
    });

    window.addEventListener('offline', () => {
      this.emit('websocket:network_offline');
    });

    // Handle page focus (for additional reliability)
    window.addEventListener('focus', () => {
      if (!this.isConnected && this.user) {
        setTimeout(() => {
          if (!this.isConnected) {
            this.connect(this.user);
          }
        }, 500);
      }
    });
  }
}

// Singleton instance
const websocketClient = new WebSocketClient();
export default websocketClient;
